import { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useGetPublicFeaturesQuery } from 'src/graphql';
import theme from 'src/theme/theme';
import { sanitationInfoIntercomArticle } from 'src/utils/intercomArticles';
import i18next, { locale } from '../../i18n';
import * as Types from '../../types';
import { Button, Divider } from '../atoms';
import { isRequiredFeatureEnabled } from '../auth';
import {
  StyledIcon,
  StyledIconInButton,
} from '../foundations/foundations.styles';
import { Footer } from '../moleculesLegacy/Footer';
import CustomHeader from './CustomHeader/CustomHeader';
import ResponsiveContainer from './ResponsiveContainer';
import {
  ButtonContainer,
  FileUploadErrorText,
  InfoContainer,
  InfoText,
  InfoWrapper,
  MobileHeaderContainer,
  SubmitSuccessContainer,
  SuccessContainer,
  SuccessHeader,
  SuccessInfoContainer,
  SuccessInfoText,
  SuccessText,
  VaultIDText,
  VaultIdContainer,
} from './SubmitSuccess.styles';
import { CustomHeaderContainer } from './questions/QuestionContainer/QuestionContainer.styles';

interface Props {
  organisation: string;
  currentUser?: Types.User;
  publicReporterAccount: Types.PublicReportingAccount | null;
  currentReport: Types.Report | null;
  selectedCountryName?: string;
}

const SubmitSuccess = (props: Props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<boolean>(false);
  const copyVaultIDText = t('Buttons.copyVaultID');
  const copiedVaultIdText = t('Buttons.copiedVaultId');
  const [buttonContent, setButtonContent] = useState<string>(copyVaultIDText);
  const submittedAtDate = props.currentReport?.date;
  const formattedDate = submittedAtDate
    ? new Date(submittedAtDate).toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  useEffect(() => {
    if (copied) {
      setButtonContent(copiedVaultIdText);
    } else {
      setButtonContent(copyVaultIDText);
    }
  }, [copied, copiedVaultIdText, copyVaultIDText]);

  const fileUploadErrors = useSelector(
    (state: Types.State) =>
      state.openReporting?.openReportingReducer?.fileUploadErrors,
  );

  const { data: publicFeatures } = useGetPublicFeaturesQuery({
    slug: props.organisation,
  });

  const reportJustSubmitted = useSelector(
    (state: Types.State) =>
      state.openReporting?.openReportingReducer?.reportJustSubmitted,
  );

  if (!reportJustSubmitted) {
    return <Redirect to={`/${props.organisation}/open-reporting`} />;
  }

  const isDataSanitationEnabled = isRequiredFeatureEnabled(
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [],
    Types.FeatureFlag.DATA_DELETION,
  );

  return (
    <SubmitSuccessContainer>
      <CustomHeaderContainer>
        <CustomHeader
          organisation={props.organisation}
          currentUser={props.currentUser}
          hideID
        />
      </CustomHeaderContainer>
      <ResponsiveContainer
        includeLeftCircleButton={false}
        includeRightCircleButton={false}
        includeBottomButton={false}
      >
        <MobileHeaderContainer>
          <CustomHeader
            organisation={props.organisation}
            currentUser={props.currentUser}
            hideID
          />
        </MobileHeaderContainer>
        <SuccessContainer>
          <SuccessHeader>
            {t('public.SubmitSuccess.successThankYou')}
          </SuccessHeader>
          {!isDataSanitationEnabled && (
            <SuccessText>
              {t('public.SubmitSuccess.successMessage')}
            </SuccessText>
          )}
          {isDataSanitationEnabled && (
            <>
              <SuccessText>
                {t('public.SubmitSuccess.successSanitationTagline')}
              </SuccessText>
              <SuccessText>
                {t('public.SubmitSuccess.successSanitationMessage')}
              </SuccessText>
              <SuccessInfoContainer
                href={sanitationInfoIntercomArticle}
                target="_blank"
                rel="noreferrer"
              >
                <StyledIcon
                  className={'uil-info-circle'}
                  size="24px"
                  color={theme.newPalette.neutral.N800}
                  isClickable={true}
                />
                <SuccessInfoText>
                  {t('public.SubmitSuccess.successInformation')}
                </SuccessInfoText>
              </SuccessInfoContainer>
              <Divider variant="horizontal" />
            </>
          )}
          <InfoWrapper>
            <InfoContainer>
              <StyledIcon className="uil uil-clipboard" size="24px" />
              <InfoText>
                <Trans
                  i18n={i18next}
                  shouldUnescape
                  i18nKey="public.SubmitSuccess.category"
                  values={{
                    category:
                      props.currentReport?.reportedCategory?.name ?? '-',
                  }}
                  components={{
                    bold: <strong />,
                  }}
                />
              </InfoText>
            </InfoContainer>
            <InfoContainer>
              <StyledIcon className="uil uil-calender" size="24px" />
              <InfoText>
                <Trans
                  i18n={i18next}
                  i18nKey="public.SubmitSuccess.dateSubmitted"
                  values={{
                    date: formattedDate ?? '-',
                  }}
                  components={{
                    bold: <strong />,
                  }}
                />
              </InfoText>
            </InfoContainer>
            {props.selectedCountryName && (
              <InfoContainer data-testid="submit-success-country">
                <StyledIcon className="uil uil-globe" size="24px" />
                <InfoText>
                  <Trans
                    i18n={i18next}
                    i18nKey="public.SubmitSuccess.country"
                    values={{ country: props.selectedCountryName }}
                    components={{ bold: <strong /> }}
                  />
                </InfoText>
              </InfoContainer>
            )}
          </InfoWrapper>

          <VaultIdContainer>
            <VaultIDText>
              VAULT ID:{' '}
              <span data-testid={'opr-submission-id'}>
                {props.publicReporterAccount?.username.toUpperCase()}
              </span>
            </VaultIDText>

            <CopyToClipboard
              text={props.publicReporterAccount?.username.toUpperCase() ?? ''}
            >
              <ButtonContainer>
                <Button
                  data-testid={'copy-vault-id'}
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 10000);
                  }}
                  width="152px"
                >
                  {!copied && (
                    <StyledIconInButton
                      size="medium"
                      position="left"
                      className="uil uil-copy"
                    />
                  )}
                  {buttonContent}
                </Button>
              </ButtonContainer>
            </CopyToClipboard>
            <SuccessText>
              {t('public.SubmitSuccess.recordYourVaultID')}
            </SuccessText>
          </VaultIdContainer>
          {fileUploadErrors !== 0 && (
            <FileUploadErrorText>
              {t('public.FileUpload.errorMessage')}
            </FileUploadErrorText>
          )}
          <Button
            variant="secondary"
            size="medium"
            onClick={() =>
              (location.href = `/${props.organisation}/open-reporting/reports`)
            }
            data-testid={'track-updates-link'}
          >
            {t('public.SubmitSuccess.viewReport')}
          </Button>
        </SuccessContainer>
      </ResponsiveContainer>

      <Footer id="desktop-powered-by-vault" />
    </SubmitSuccessContainer>
  );
};

export default SubmitSuccess;
